// Tag input for 6-digit pincodes — Enter or comma adds a tag; click × removes.

import { useState, type KeyboardEvent, type JSX } from "react";

interface Props {
  value: string[];
  onChange: (v: string[]) => void;
  error?: string;
}

export function PincodeTagInput({ value, onChange, error }: Props): JSX.Element {
  const [input, setInput] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);

  function commit(): void {
    const raw = input.trim().replace(/,/g, "");
    if (raw === "") return;
    if (!/^\d{6}$/.test(raw)) { setInputError("Pincodes must be exactly 6 digits."); return; }
    if (!value.includes(raw)) onChange([...value, raw]);
    setInput("");
    setInputError(null);
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); return; }
    if (e.key === "Backspace" && input === "" && value.length > 0) onChange(value.slice(0, -1));
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">Coverage pincodes</label>
      <div
        className={`flex flex-wrap gap-1 p-2 border rounded-lg min-h-[2.5rem] focus-within:ring-2 focus-within:ring-navy-500 ${
          error ? "border-red-400" : "border-slate-300"
        }`}
      >
        {value.map((pin) => (
          <span key={pin} className="flex items-center gap-1 bg-navy-100 text-navy-900 text-xs px-2 py-0.5 rounded-full">
            {pin}
            <button
              type="button"
              onClick={() => onChange(value.filter((v) => v !== pin))}
              aria-label={`Remove ${pin}`}
              className="text-navy-500 hover:text-navy-950 font-bold leading-none"
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          onBlur={commit}
          placeholder={value.length === 0 ? "Enter 6-digit pincode, press Enter" : ""}
          inputMode="numeric"
          maxLength={6}
          className="flex-1 min-w-[10rem] text-sm outline-none bg-transparent"
        />
      </div>
      {(error ?? inputError) && <p className="mt-1 text-xs text-red-500">{error ?? inputError}</p>}
      <p className="mt-1 text-xs text-slate-400">Press Enter or comma after each 6-digit pincode</p>
    </div>
  );
}
